/* eslint-disable array-callback-return */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from "react";
import {
  Container,
  Segment,
  Header,
  Icon,
  Grid,
  Label,
  Message,
  Button,
  Table,
  Input,
  Modal,
  Dimmer,
  Loader,
  Popup,
} from "semantic-ui-react";
import moment from "moment";
import { PDFViewer } from "@react-pdf/renderer";

import { db } from "../firebase/firebaseConfig";
import { getDocs, collection, where, query } from "firebase/firestore";
import { UserContext } from "../context/UserContext";
import ReporteFinal from "./ReporteFinalGarantiaPdf";

const COLORS = {
  teal: "#00b5ad",
  tealSoft: "#e8f8f7",
  green: "#21ba45",
  greenSoft: "#edf9f0",
  orange: "#f2711c",
  orangeSoft: "#fff5eb",
  red: "#db2828",
  border: "#e5e7eb",
  text: "#1f2937",
  muted: "#6b7280",
  background: "#f8fafc",
};

const cardStyle = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  boxShadow: "0 1px 3px rgba(0,0,0,.06)",
};

const stickyTh = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: "#f8fafc",
  color: "#374151",
  whiteSpace: "nowrap",
};

const safeToText = (v) => {
  if (v === null || v === undefined) return "";
  if (v?.toDate) return moment(v.toDate()).format("DD/MM/YYYY HH:mm");

  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }

  return String(v);
};

const ReporteGarantiaPRO = () => {
  const { users } = useContext(UserContext);

  // -------------------- data --------------------
  const [asignar, setAsignar] = useState([]);
  const [usuarioIngreso, setUsuarioIngreso] = useState([]);

  // -------------------- UI --------------------
  const [loading, setLoading] = useState(true);
  const [buscador, setBuscador] = useState("");

  // -------------------- PDF --------------------
  const [openPdf, setOpenPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const [cabecera, setCabecera] = useState([]);
  const [garantiaCab, setGarantiaCab] = useState(null);
  const [instru, setInstru] = useState([]);
  const [check, setCheck] = useState([]);
  const [medicion, setMedicion] = useState([]);
  const [seguridad, setSeguridad] = useState([]);
  const [servicio, setServicio] = useState([]);
  const [repuestos, setRepuestos] = useState([]);
  const [falla, setFalla] = useState([]);
  const [userAdd, setUsersAdd] = useState([]);

  const formatearFecha = (fecha) => {
    if (!fecha?.toDate) return "";
    return moment(fecha.toDate()).format("DD/MM/YYYY HH:mm");
  };

  // -------------------- Load list --------------------
  const getIngresostcab = useCallback(async () => {
    if (!users?.emp_id) return;

    const q = query(
      collection(db, "ingresostcab"),
      where("emp_id", "==", users.emp_id),
      where("estado", "==", "ENTREGADO")
    );

    const snap = await getDocs(q);
    setAsignar(
      snap.docs.map((d) => ({
        ...d.data(),
        id: d.id,
      }))
    );
  }, [users?.emp_id]);

  const consultarUsuario = useCallback(async () => {
    if (!users?.emp_id) return;

    const q = query(
      collection(db, "usuarios"),
      where("emp_id", "==", users.emp_id)
    );

    const snap = await getDocs(q);
    setUsuarioIngreso(
      snap.docs.map((d) => ({
        ...d.data(),
        id: d.id,
      }))
    );
  }, [users?.emp_id]);

  useEffect(() => {
    const init = async () => {
      if (!users?.emp_id) return;

      setLoading(true);

      try {
        await Promise.all([
          getIngresostcab(),
          consultarUsuario(),
        ]);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [users?.emp_id, getIngresostcab, consultarUsuario]);

  // -------------------- Garantías --------------------
  const dataGarantiaOrdenada = useMemo(() => {
    const garantia = asignar.filter(
      (x) => x.servicionew === "GARANTIA"
    );

    return [...garantia].sort(
      (a, b) => (b?.fecha_out || 0) - (a?.fecha_out || 0)
    );
  }, [asignar]);

  // -------------------- Search --------------------
  const filteredData = useMemo(() => {
    const q = (buscador || "").trim().toLowerCase();

    if (!q) return dataGarantiaOrdenada;

    return dataGarantiaOrdenada.filter((item) =>
      Object.keys(item).some((k) =>
        safeToText(item[k]).toLowerCase().includes(q)
      )
    );
  }, [dataGarantiaOrdenada, buscador]);

  // -------------------- KPIs --------------------
  const kpis = useMemo(() => {
    const total = dataGarantiaOrdenada.length;

    const certificadosOk = dataGarantiaOrdenada.filter(
      (x) => x.estado_servicio === "REALIZADO"
    ).length;

    return {
      total,
      certificadosOk,
      sinCert: total - certificadosOk,
    };
  }, [dataGarantiaOrdenada]);

  // -------------------- PDF --------------------
  const verPdfReporte = useCallback(
    async (idCab) => {
      if (!users?.emp_id) return;

      setPdfLoading(true);
      setOpenPdf(true);

      try {
        const cab = dataGarantiaOrdenada.filter(
          (x) => x.id === idCab
        );

        setCabecera(cab);

        const qUsuarioEjec = query(
          collection(db, "bitacoracabgarantia"),
          where("emp_id", "==", users.emp_id),
          where("id_cab_inst", "==", idCab)
        );

        const qDiag = query(
          collection(db, "bitacoragarantia"),
          where("emp_id", "==", users.emp_id),
          where("id_cab_inst", "==", idCab)
        );

        const qEjec = query(
          collection(db, "bitacoragarantiaeject"),
          where("emp_id", "==", users.emp_id),
          where("id_cab_inst", "==", idCab)
        );

        const [snapUsuario, snapDiag, snapEjec] =
          await Promise.all([
            getDocs(qUsuarioEjec),
            getDocs(qDiag),
            getDocs(qEjec),
          ]);

        const usuarioEjec = snapUsuario.docs.map((d) => ({
          ...d.data(),
          id: d.id,
        }));

        const diag = snapDiag.docs.map((d) => ({
          ...d.data(),
          id: d.id,
        }));

        const ejec = snapEjec.docs.map((d) => ({
          ...d.data(),
          id: d.id,
        }));

        setServicio(
          diag.filter((p) => p.categoria === "SERVICIO")
        );

        setRepuestos(
          diag.filter((p) => p.categoria === "REPUESTO")
        );

        setFalla(
          diag.filter((f) => f.categoria === "FALLA")
        );

        const garantiaCabs = snapUsuario.empty
          ? null
          : {
              id: snapUsuario.docs[0].id,
              ...snapUsuario.docs[0].data(),
            };

        setGarantiaCab(garantiaCabs);

        const porCat = (cat) =>
          ejec
            .filter((x) => x.categoria === cat)
            .sort(
              (a, b) =>
                (a?.fechaadd || 0) -
                (b?.fechaadd || 0)
            );

        setInstru(porCat("INSTRUMENTOS"));
        setCheck(porCat("CHECK"));
        setMedicion(porCat("MEDICION"));
        setSeguridad(porCat("SEGURIDAD"));

        const usermod =
          usuarioEjec?.[0]?.usermod || "";

        setUsersAdd(
          usuarioIngreso.filter(
            (u) => u.correo === usermod
          )
        );
      } catch (e) {
        console.error(e);
      } finally {
        setPdfLoading(false);
      }
    },
    [
      users?.emp_id,
      dataGarantiaOrdenada,
      usuarioIngreso,
    ]
  );

  // -------------------- Estado --------------------
  const estadoLabel = (estado) => {
    if (estado === "REALIZADO") {
      return (
        <Label basic color="green" size="tiny">
          REALIZADO
        </Label>
      );
    }

    if (!estado) {
      return (
        <Label basic size="tiny">
          -
        </Label>
      );
    }

    return (
      <Label basic color="red" size="tiny">
        {estado}
      </Label>
    );
  };

  // -------------------- Render --------------------
  return (
    <Container
      fluid
      style={{
        background: COLORS.background,
        minHeight: "100vh",
        padding: "18px 16px 35px",
      }}
    >
      <Container fluid style={{ maxWidth: 1400 }}>

        {/* CABECERA */}
        <Segment
          style={{
            ...cardStyle,
            padding: "18px 20px",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            <Label
              size="massive"
              style={{
                background: COLORS.tealSoft,
                color: COLORS.teal,
                margin: 0,
              }}
            >
              <Icon
                name="shield alternate"
                style={{ margin: 0 }}
              />
            </Label>

            <Header
              as="h2"
              style={{
                margin: 0,
                color: COLORS.text,
                fontSize: 21,
              }}
            >
              Garantías realizadas

              <Header.Subheader
                style={{ marginTop: 5 }}
              >
                Consulta de garantías y certificados de
                Servicio Técnico
              </Header.Subheader>
            </Header>
          </div>
        </Segment>

        {/* KPIs */}
        <Grid
          stackable
          columns={3}
          style={{ marginBottom: 8 }}
        >
          <Grid.Column>
            <Segment
              style={{
                ...cardStyle,
                margin: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <Label
                  size="large"
                  style={{
                    background: COLORS.tealSoft,
                    color: COLORS.teal,
                  }}
                >
                  <Icon name="shield alternate" />
                </Label>

                <div>
                  <div
                    style={{
                      color: COLORS.muted,
                      fontSize: 12,
                    }}
                  >
                    Total garantías
                  </div>

                  <strong
                    style={{
                      fontSize: 23,
                      color: COLORS.text,
                    }}
                  >
                    {kpis.total}
                  </strong>
                </div>
              </div>
            </Segment>
          </Grid.Column>

          <Grid.Column>
            <Segment
              style={{
                ...cardStyle,
                margin: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <Label
                  size="large"
                  style={{
                    background: COLORS.greenSoft,
                    color: COLORS.green,
                  }}
                >
                  <Icon name="check circle outline" />
                </Label>

                <div>
                  <div
                    style={{
                      color: COLORS.muted,
                      fontSize: 12,
                    }}
                  >
                    Con certificado
                  </div>

                  <strong
                    style={{
                      fontSize: 23,
                      color: COLORS.text,
                    }}
                  >
                    {kpis.certificadosOk}
                  </strong>
                </div>
              </div>
            </Segment>
          </Grid.Column>

          <Grid.Column>
            <Segment
              style={{
                ...cardStyle,
                margin: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <Label
                  size="large"
                  style={{
                    background: COLORS.orangeSoft,
                    color: COLORS.orange,
                  }}
                >
                  <Icon name="clock outline" />
                </Label>

                <div>
                  <div
                    style={{
                      color: COLORS.muted,
                      fontSize: 12,
                    }}
                  >
                    Sin certificado
                  </div>

                  <strong
                    style={{
                      fontSize: 23,
                      color: COLORS.text,
                    }}
                  >
                    {kpis.sinCert}
                  </strong>
                </div>
              </div>
            </Segment>
          </Grid.Column>
        </Grid>

        {/* BUSCADOR */}
        <Segment
          style={{
            ...cardStyle,
            marginTop: 12,
          }}
        >
          <Grid stackable verticalAlign="middle">
            <Grid.Row>
              <Grid.Column width={13}>
                <Header
                  as="h4"
                  style={{ marginBottom: 10 }}
                >
                  <Icon name="filter" color="teal" />

                  <Header.Content>
                    Búsqueda de garantías

                    <Header.Subheader>
                      Orden, RUT, cliente, equipo, serie,
                      estado u otro dato
                    </Header.Subheader>
                  </Header.Content>
                </Header>

                <Input
                  fluid
                  focus
                  placeholder="Escriba para buscar..."
                  value={buscador}
                  onChange={(e) =>
                    setBuscador(e.target.value)
                  }
                  icon={{
                    name: buscador.trim()
                      ? "close"
                      : "search",
                    link: true,
                    onClick: () =>
                      buscador.trim()
                        ? setBuscador("")
                        : null,
                  }}
                />
              </Grid.Column>

              <Grid.Column width={3}>
                <Button
                  basic
                  fluid
                  icon
                  labelPosition="left"
                  onClick={() => setBuscador("")}
                  disabled={!buscador}
                >
                  <Icon name="eraser" />
                  Limpiar
                </Button>
              </Grid.Column>
            </Grid.Row>

            {!!buscador.trim() && (
              <Grid.Row>
                <Grid.Column width={16}>
                  <Message
                    info
                    size="small"
                    style={{ margin: 0 }}
                  >
                    <Icon name="info circle" />
                    Mostrando{" "}
                    <b>{filteredData.length}</b>{" "}
                    resultado(s) para{" "}
                    <b>{buscador}</b>.
                  </Message>
                </Grid.Column>
              </Grid.Row>
            )}
          </Grid>
        </Segment>

        {/* RESULTADOS */}
        <Segment
          raised
          style={{
            ...cardStyle,
            padding: 0,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "13px 16px",
              borderBottom: `1px solid ${COLORS.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Header as="h4" style={{ margin: 0 }}>
              <Icon
                name="list alternate outline"
                color="teal"
              />

              <Header.Content>
                Garantías realizadas

                <Header.Subheader>
                  Garantías y certificados asociados
                </Header.Subheader>
              </Header.Content>
            </Header>

            <Label basic color="teal">
              {filteredData.length} registro(s)
            </Label>
          </div>

          {loading ? (
            <div
              style={{
                height: 220,
                position: "relative",
              }}
            >
              <Dimmer active inverted>
                <Loader size="large">
                  Procesando...
                </Loader>
              </Dimmer>
            </div>
          ) : filteredData.length === 0 ? (
            <Message
              warning
              icon
              style={{ margin: 16 }}
            >
              <Icon name="warning circle" />

              <Message.Content>
                <Message.Header>
                  Sin registros
                </Message.Header>

                {buscador.trim()
                  ? "No se encontraron coincidencias para la búsqueda."
                  : "No existen garantías realizadas."}
              </Message.Content>
            </Message>
          ) : (
            <div
              style={{
                overflowX: "auto",
                overflowY: "auto",
                maxHeight: 520,
              }}
            >
              <Table
                celled
                compact="very"
                striped
                selectable
                singleLine
                style={{
                  fontSize: 11,
                  margin: 0,
                }}
              >
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell style={stickyTh}>
                      #
                    </Table.HeaderCell>

                    <Table.HeaderCell style={stickyTh}>
                      Orden
                    </Table.HeaderCell>

                    <Table.HeaderCell style={stickyTh}>
                      RUT
                    </Table.HeaderCell>

                    <Table.HeaderCell style={stickyTh}>
                      Nombre
                    </Table.HeaderCell>

                    <Table.HeaderCell style={stickyTh}>
                      Fecha cierre
                    </Table.HeaderCell>

                    <Table.HeaderCell style={stickyTh}>
                      Fecha entrega
                    </Table.HeaderCell>

                    <Table.HeaderCell style={stickyTh}>
                      Equipo
                    </Table.HeaderCell>

                    <Table.HeaderCell style={stickyTh}>
                      Serie
                    </Table.HeaderCell>

                    <Table.HeaderCell style={stickyTh}>
                      Estado
                    </Table.HeaderCell>

                    <Table.HeaderCell
                      style={stickyTh}
                      textAlign="center"
                    >
                      Certificado
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {filteredData.map((item, index) => {
                    const disponible =
                      item.estado_servicio ===
                      "REALIZADO";

                    return (
                      <Table.Row
                        key={item.id ?? index}
                      >
                        <Table.Cell>
                          {index + 1}
                        </Table.Cell>

                        <Table.Cell
                          style={{ fontWeight: 600 }}
                        >
                          {item.folio}
                        </Table.Cell>

                        <Table.Cell>
                          {item.rut || "-"}
                        </Table.Cell>

                        <Table.Cell
                          style={{
                            whiteSpace: "normal",
                            fontWeight: 600,
                          }}
                        >
                          {item.entidad || "-"}
                        </Table.Cell>

                        <Table.Cell
                          style={{
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.fecha_out
                            ? formatearFecha(
                                item.fecha_out
                              )
                            : "-"}
                        </Table.Cell>

                        <Table.Cell
                          style={{
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.fechamod
                            ? formatearFecha(
                                item.fechamod
                              )
                            : "-"}
                        </Table.Cell>

                        <Table.Cell
                          style={{
                            whiteSpace: "normal",
                          }}
                        >
                          {item.tipo || "-"}
                        </Table.Cell>

                        <Table.Cell>
                          {item.serie || "-"}
                        </Table.Cell>

                        <Table.Cell>
                          {estadoLabel(
                            item.estado_servicio
                          )}
                        </Table.Cell>

                        <Table.Cell textAlign="center">
                          {disponible ? (
                            <Popup
                              content="Ver certificado"
                              position="top center"
                              trigger={
                                <Button
                                  basic
                                  compact
                                  icon
                                  size="mini"
                                  onClick={() =>
                                    verPdfReporte(
                                      item.id
                                    )
                                  }
                                  style={{
                                    color: COLORS.red,
                                    background: "#fff",
                                    boxShadow: "none",
                                  }}
                                >
                                  <Icon
                                    name="file pdf outline"
                                    color="red"
                                    style={{
                                      margin: 0,
                                      fontSize: "1.25em",
                                    }}
                                  />
                                </Button>
                              }
                            />
                          ) : (
                            <Popup
                              content="Certificado no disponible"
                              position="top center"
                              trigger={
                                <span>
                                  <Button
                                    basic
                                    compact
                                    icon
                                    size="mini"
                                    disabled
                                  >
                                    <Icon
                                      name="file pdf outline"
                                      style={{
                                        margin: 0,
                                        fontSize:
                                          "1.25em",
                                      }}
                                    />
                                  </Button>
                                </span>
                              }
                            />
                          )}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </div>
          )}
        </Segment>

        {/* MODAL PDF */}
        <Modal
          open={openPdf}
          onClose={() => setOpenPdf(false)}
          size="large"
          closeIcon
          closeOnDimmerClick={false}
          closeOnEscape
        >
          <Modal.Header>
            <Icon
              name="file pdf outline"
              color="red"
            />

            Certificado de Garantía

            {cabecera?.[0]?.folio && (
              <Label
                basic
                style={{ marginLeft: 10 }}
              >
                Folio {cabecera[0].folio}
              </Label>
            )}
          </Modal.Header>

          <Modal.Content style={{ padding: 0 }}>
            {pdfLoading ? (
              <div style={{ height: 560 }}>
                <Dimmer active inverted>
                  <Loader size="large">
                    Procesando...
                  </Loader>
                </Dimmer>
              </div>
            ) : (
              <PDFViewer
                width="100%"
                height="560"
                style={{
                  border: "1px solid #ddd",
                }}
              >
                <ReporteFinal
                  cabecera={cabecera}
                  falla={falla}
                  repuestos={repuestos}
                  servicio={servicio}
                  instrumento={instru}
                  check={check}
                  medicion={medicion}
                  seguridad={seguridad}
                  usuario={userAdd}
                  empresa={users?.emp_id}
                  garantiaCab={garantiaCab}
                />
              </PDFViewer>
            )}
          </Modal.Content>

          <Modal.Actions>
            <Button
              color="teal"
              icon
              labelPosition="left"
              onClick={() => setOpenPdf(false)}
            >
              <Icon name="close" />
              Cerrar
            </Button>
          </Modal.Actions>
        </Modal>
      </Container>
    </Container>
  );
};

export default ReporteGarantiaPRO;